import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { LegacyOnboardingRedirect } from './features/webAppOnboarding/routing/LegacyOnboardingRedirect';
import { ShortInviteLinkEntry } from './features/webAppOnboarding/routing/ShortInviteLinkEntry';
import { WebAppOnboardingLegacyUrlRedirect } from './features/webAppOnboarding/routing/WebAppOnboardingLegacyUrlRedirect';
import { WebAppOnboardingApp } from './features/webAppOnboarding/WebAppOnboardingApp';
import { PatientAppNotInstalled } from './pages/AppNotInstalled/PatientAppNotInstalled';
import { AppUserEmailVerification } from './pages/AppUserEmailVerification/AppUserEmailVerification';
import { DocumentDownload } from './pages/DocumentDownload/DocumentDownload';
import { EmailUnsubscribe } from './pages/EmailUnsubscribe/EmailUnsubscribe';
import { ErrorOverlay } from './pages/ErrorOverlay';
import { PracticeUserEmailVerification } from './pages/PracticeUserEmailVerification/PracticeUserEmailVerification';
import { SignatureRecapturePage } from './pages/SignatureRecapture';
import { UserAppRating } from './pages/UserAppRating/UserAppRating';
import { UserAppRatingCompleted } from './pages/UserAppRatingCompleted/UserAppRatingCompleted';
import { WidgetTest } from './pages/WidgetTest/WidgetTest';
import { GlobalStyle } from './styles/GlobalStyle';

// ERS-31: the course-payment callables and the recovered BNPL v1 flow have no auth or ownership
// check yet (ERS-25), so their routes must never be reachable from production hosting. MODE — not
// DEV — is the gate: `import.meta.env.DEV` is false in every `vite build`, which would also remove
// them from io-physiofit-development.web.app and block the ERS-28 E2E run.
const isProductionBuild = import.meta.env.MODE === 'production';

// Dynamic imports, so these pages are their own chunks and never reach the production entry bundle.
// A static import plus the runtime gate below leaves that to the bundler proving a styled-components
// module tree side-effect free, which it is not obliged to do.
const UiLabPage = lazy(() =>
  import('./features/webAppOnboarding/dev/uiLab/UiLabPage').then((m) => ({ default: m.UiLabPage })),
);
// PF-6119: calibration harness for the signature ink thresholds. LOCAL DEV ONLY — stricter than
// `DevOnlyRoute`, which keeps its routes on development hosting on purpose (ERS-31). This one must
// not be reachable on any deployed origin at all: it exposes the live threshold knobs, and a URL
// that is merely obscure is still a URL a patient could be sent. `import.meta.env.DEV` is false in
// every `vite build`, mode irrelevant, so the ternary collapses to null and the dynamic import
// below is dropped from the bundle entirely rather than shipped as an unreachable chunk.
const InkCalibrationPage = import.meta.env.DEV
  ? lazy(() => import('./pages/SignatureRecapture/dev/InkCalibrationPage').then((m) => ({ default: m.InkCalibrationPage })))
  : null;
const LegacyBnplV1Router = lazy(() =>
  import('./features/webAppOnboarding/dev/legacyBnplV1/OnboardingRouter').then((m) => ({
    default: m.OnboardingRouter,
  })),
);
const BNPLSetup = lazy(() => import('./pages/BNPLSetup/BNPLSetup').then((m) => ({ default: m.BNPLSetup })));
const BNPLTest = lazy(() => import('./pages/BNPLTest/BNPLTest').then((m) => ({ default: m.BNPLTest })));
const CoursePaymentCallablesSetup = lazy(() =>
  import('./pages/CoursePaymentCallablesSetup/CoursePaymentCallablesSetup').then((m) => ({
    default: m.CoursePaymentCallablesSetup,
  })),
);
const CoursePaymentCallablesTest = lazy(() =>
  import('./pages/CoursePaymentCallablesTest/CoursePaymentCallablesTest').then((m) => ({
    default: m.CoursePaymentCallablesTest,
  })),
);

/** Renders a dev-only page, or redirects on a production build. */
function DevOnlyRoute({ children }: { children: ReactNode }) {
  if (isProductionBuild) {
    return <Navigate to="/" replace />;
  }
  return <Suspense fallback={null}>{children}</Suspense>;
}

function App() {
  const router = createBrowserRouter([
    {
      path: 'widget-test',
      element: <WidgetTest />,
    },
    {
      path: '/o/:token',
      element: <ShortInviteLinkEntry />,
    },
    {
      path: '/onboarding/*',
      element: <LegacyOnboardingRedirect />,
    },
    {
      path: '/webAppOnboarding/v2',
      element: <Navigate to="/webAppOnboarding" replace />,
    },
    {
      path: '/webAppOnboarding/v2/*',
      element: <WebAppOnboardingLegacyUrlRedirect />,
    },
    {
      // ERS-31: recovered BNPL v1 flow (docs/ERS-5/ERS-31/). Dev builds only — it calls the three
      // course-payment callables, which have no auth or ownership check yet (ERS-25). Must stay
      // above the '/webAppOnboarding/*' entry below or the live router swallows it.
      path: '/webAppOnboarding/v1/*',
      element: (
        <DevOnlyRoute>
          <LegacyBnplV1Router />
        </DevOnlyRoute>
      ),
    },
    {
      path: '/webAppOnboarding/*',
      element: <WebAppOnboardingApp />,
    },
    {
      path: '/documents/:userId/:courseId/:fileName',
      element: <DocumentDownload />,
    },
    {
      // PF-6119: ships to production. Safety is the server-side `appInfo/signatureRecapture`
      // allowlist, not route hiding — never wrap this in `DevOnlyRoute`.
      path: '/signature-recapture/:recaptureId',
      element: <SignatureRecapturePage />,
    },
    {
      path: '/onboarding-ui-lab',
      element: (
        <DevOnlyRoute>
          <UiLabPage />
        </DevOnlyRoute>
      ),
    },
    ...(InkCalibrationPage
      ? [
          {
            path: '/signature-recapture-calibration',
            element: (
              <Suspense fallback={null}>
                <InkCalibrationPage />
              </Suspense>
            ),
          },
        ]
      : []),
    {
      path: '/',
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: <ErrorOverlay />,
        },
        {
          path: 'bnpl',
          element: (
            <DevOnlyRoute>
              <BNPLSetup />
            </DevOnlyRoute>
          ),
        },
        {
          path: 'bnpl/test',
          element: (
            <DevOnlyRoute>
              <BNPLTest />
            </DevOnlyRoute>
          ),
        },
        {
          path: 'bnpl/course-payment/setup',
          element: (
            <DevOnlyRoute>
              <CoursePaymentCallablesSetup />
            </DevOnlyRoute>
          ),
        },
        {
          path: 'bnpl/course-payment/test',
          element: (
            <DevOnlyRoute>
              <CoursePaymentCallablesTest />
            </DevOnlyRoute>
          ),
        },
        {
          path: 'app-not-installed',
          element: <PatientAppNotInstalled />,
        },
        {
          path: 'user-app-rating/store-rating/:storePlatform',
          element: <UserAppRating />,
        },
        {
          path: 'user-app-rating/store-rating/:storePlatform/completed',
          element: <UserAppRatingCompleted />,
        },
        {
          path: 'user-app-rating-completed',
          element: <UserAppRatingCompleted />,
        },
        {
          path: 'app-user-email-verification',
          element: <AppUserEmailVerification />,
        },
        {
          path: 'practice-user-email-verification',
          element: <PracticeUserEmailVerification />,
        },
        {
          path: 'email-unsubscribe/:userId/:env',
          element: <EmailUnsubscribe />,
        },
        { path: '*', element: <ErrorOverlay /> },
      ],
    },
  ]);

  return (
    <>
      <GlobalStyle />
      <RouterProvider router={router} />
    </>
  );
}

export default App;
